<template>
  <div class="p-4 mybalance">
    <!-- Solde Widget -->
    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <h2 class="text-lg font-semibold text-gray-700 mb-4">Ma balance</h2>
      <div class="flex flex-wrap gap-6">
        <div class="flex-1 min-w-[200px] bg-green-50 rounded-lg p-4 text-center">
          <p class="text-sm text-gray-500 mb-1">Solde disponible</p>
          <p class="text-2xl font-bold text-green-600">{{ walletDisplay }}</p>
        </div>
        <div class="flex-1 min-w-[200px] bg-blue-50 rounded-lg p-4 text-center">
          <p class="text-sm text-gray-500 mb-1">Méthode de paiement</p>
          <p class="text-lg font-semibold text-blue-600">{{ paymentMethodDisplay }}</p>
        </div>
      </div>
    </div>

    <!-- Historique -->
    <div class="bg-white rounded-lg shadow">
      <div class="p-4 border-b flex items-center justify-between">
        <h3 class="text-lg font-semibold text-gray-700">Historique des crédits</h3>
        <span class="text-sm text-gray-400">{{ totalHistories }} résultat(s)</span>
      </div>
      <div v-if="loading" class="p-8 text-center text-gray-400">Chargement...</div>
      <div v-else-if="histories.length === 0" class="p-8 text-center text-gray-400">Aucun historique</div>
      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-gray-500 uppercase text-xs">
            <tr>
              <th class="p-3 text-left">Date</th>
              <th class="p-3 text-center">Opération</th>
              <th class="p-3 text-left">Commentaire</th>
              <th class="p-3 text-right">Montant</th>
              <th class="p-3 text-center">Statut</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(h, i) in histories" :key="h._id" :class="i % 2 === 0 ? 'bg-white' : 'bg-gray-50'" class="border-t hover:bg-gray-100 transition-colors">
              <td class="p-3 text-gray-600 whitespace-nowrap">{{ formatDate(h.date || h.createdAt) }}</td>
              <td class="p-3 text-center">
                <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
                  :class="isCredit(h) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                  <i class="material-icons" style="font-size:14px;">{{ isCredit(h) ? 'arrow_downward' : 'arrow_upward' }}</i>
                  {{ isCredit(h) ? 'Entrée' : 'Sortie' }}
                </span>
              </td>
              <td class="p-3 text-gray-600">
                {{ h.comment || '-' }}
                <button v-if="h.orders && h.orders.length" @click="toggleCpaDetails(h)" class="ml-2 inline-flex items-center text-green-600 hover:text-green-800" title="Voir les produits">
                  <i class="material-icons" style="font-size:18px;">{{ expandedHistory === h._id ? 'visibility_off' : 'visibility' }}</i>
                </button>
                <div v-if="expandedHistory === h._id && cpaOrderDetails.length" class="mt-2 bg-gray-50 rounded-lg p-3 text-xs">
                  <table class="w-full">
                    <thead class="text-gray-500 uppercase">
                      <tr>
                        <th class="pb-1 text-left">#</th>
                        <th class="pb-1 text-left">Produit</th>
                        <th class="pb-1 text-center">Qté</th>
                        <th class="pb-1 text-right">Prix d'achat</th>
                        <th class="pb-1 text-center">Commission ML</th>
                        <th class="pb-1 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(od, idx) in cpaOrderDetails" :key="od._id" class="border-t border-gray-200">
                        <td class="py-1 text-gray-500">{{ idx + 1 }}</td>
                        <td class="py-1">{{ od.productName }}</td>
                        <td class="py-1 text-center">{{ od.quantity }}</td>
                        <td class="py-1 text-right">{{ od.purchasePriceBrut }}</td>
                        <td class="py-1 text-center text-red-500">{{ od.mlCommissionRate }}%</td>
                        <td class="py-1 text-right font-semibold">{{ od.purchasePrice }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="expandedHistory === h._id && loadingCpaDetails" class="mt-2 text-xs text-gray-400">Chargement...</div>
              </td>
              <td class="p-3 text-right font-semibold whitespace-nowrap">
                <span :class="isCredit(h) ? 'text-green-600' : 'text-red-600'">
                  {{ isCredit(h) ? '+' : '-' }}{{ formatAmount(h) }}
                </span>
              </td>
              <td class="p-3 text-center">
                <span class="inline-block px-2.5 py-1 rounded-full text-xs font-semibold"
                  :class="statusClass(h.status)">
                  {{ statusLabel(h.status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Pagination -->
      <div v-if="totalPages > 1" class="p-4 flex items-center justify-center gap-2 border-t">
        <button @click="changePage(currentPage - 1)" :disabled="currentPage <= 1"
          class="px-3 py-1 rounded border text-sm" :class="currentPage <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'">
          Précédent
        </button>
        <template v-for="(p, idx) in visiblePages">
          <span v-if="p === '...'" :key="'dots-'+idx" class="px-2 text-gray-400">...</span>
          <button v-else :key="'p-'+p" @click="changePage(p)"
            class="px-3 py-1 rounded border text-sm"
            :class="p === currentPage ? 'bg-green-600 text-white border-green-600' : 'text-gray-600 hover:bg-gray-100'">
            {{ p }}
          </button>
        </template>
        <button @click="changePage(currentPage + 1)" :disabled="currentPage >= totalPages"
          class="px-3 py-1 rounded border text-sm" :class="currentPage >= totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'">
          Suivant
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      wallet: null,
      histories: [],
      loading: true,
      currentPage: 1,
      perPage: 20,
      totalHistories: 0,
      totalPages: 1,
      expandedHistory: null,
      cpaOrderDetails: [],
      loadingCpaDetails: false,
    };
  },
  computed: {
    walletDisplay() {
      if (!this.wallet) return '—';
      const cur = this.wallet.defaultCurrency || 'MAD';
      const amount = cur === 'USD' ? this.wallet.amount : this.wallet.amountMAD;
      return (amount || 0).toFixed(2) + ' ' + cur;
    },
    paymentMethodDisplay() {
      const pm = this.currentUser && this.currentUser.paymentMethod;
      if (!pm || !pm.type) return 'Non configuré';
      const labels = { bank: 'Virement bancaire', payoneer: 'Payoneer', wise: 'Wise', usdt: 'USDT' };
      return labels[pm.type] || pm.type;
    },
    visiblePages() {
      const total = this.totalPages;
      const cur = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [];
      pages.push(1);
      if (cur > 3) pages.push('...');
      for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) {
        pages.push(i);
      }
      if (cur < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    },
  },
  async mounted() {
    await this.loadWallet();
    await this.loadHistories();
  },
  watch: {
    idWharhouse: async function () {
      await this.loadWallet();
      await this.loadHistories();
    },
  },
  methods: {
    isCredit(h) {
      return h.operationType === 'in' || h.operationType === 'credit';
    },
    async loadWallet() {
      try {
        const res = await this.$server.search("balances", { seller: this.currentUser._id });
        if (res && res.content && res.content.results && res.content.results.length > 0) {
          this.wallet = res.content.results[0];
        }
      } catch (e) {
        console.log('Error loading wallet:', e);
      }
    },
    async loadHistories() {
      this.loading = true;
      try {
        const res = await this.$server.search("balancesHistories", {
          seller: this.currentUser._id,
          limit: this.perPage,
          page: this.currentPage,
        });
        if (res && res.content && res.content.results) {
          this.histories = res.content.results;
          this.totalHistories = res.content.total || 0;
          this.totalPages = res.content.last_page || 1;
        } else {
          this.histories = [];
        }
      } catch (e) {
        console.log('Error loading histories:', e);
        this.histories = [];
      }
      this.loading = false;
    },
    async changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      await this.loadHistories();
    },
    formatDate(d) {
      if (!d) return '-';
      const dt = new Date(d);
      return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' + dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    },
    formatAmount(h) {
      if (!h) return '-';
      const cur = this.wallet && this.wallet.defaultCurrency || 'MAD';
      const amt = cur === 'USD' ? (h.amount || 0) : (h.amountMAD || h.amount || 0);
      return (typeof amt === 'number' ? amt.toFixed(2) : amt) + ' ' + cur;
    },
    statusLabel(status) {
      const labels = { paid: 'Payé', pending: 'En attente', approved: 'Approuvé', completed: 'Complété', rejected: 'Rejeté' };
      return labels[status] || status || '-';
    },
    async toggleCpaDetails(h) {
      if (this.expandedHistory === h._id) {
        this.expandedHistory = null;
        this.cpaOrderDetails = [];
        return;
      }
      this.expandedHistory = h._id;
      this.cpaOrderDetails = [];
      this.loadingCpaDetails = true;
      try {
        const details = [];
        for (const orderId of h.orders) {
          const res = await this.$server.get("orders", { id: orderId });
          if (res && res.content) {
            const o = res.content;
            const productNames = (o.details || []).map(d => d.product && (d.product.name || d.product) || '-').join(', ');
            const qty = (o.details || []).reduce((sum, d) => sum + (d.quantity || 0), 0);
            const ppBrut = parseFloat((o.cpa && o.cpa.purchasePrice) || 0);
            const mlRate = parseFloat((o.cpa && o.cpa.mlCommissionRate) || 0);
            const supplierShareUnit = ppBrut - (ppBrut * mlRate / 100);
            const supplierShareTotal = supplierShareUnit * qty;
            details.push({
              _id: o._id,
              id: o.id,
              productName: productNames,
              quantity: qty,
              purchasePriceBrut: ppBrut,
              supplierShareUnit: Math.floor(supplierShareUnit * 100) / 100,
              mlCommissionRate: mlRate,
              purchasePrice: Math.floor(supplierShareTotal * 100) / 100,
            });
          }
        }
        this.cpaOrderDetails = details;
      } catch (e) { /* noop */ }
      this.loadingCpaDetails = false;
    },
    statusClass(status) {
      if (status === 'paid' || status === 'approved' || status === 'completed') return 'bg-green-100 text-green-700';
      if (status === 'pending') return 'bg-yellow-100 text-yellow-700';
      if (status === 'rejected') return 'bg-red-100 text-red-700';
      return 'bg-gray-100 text-gray-600';
    },
  },
};
</script>
